/**
 * 거래 문서함 — 거래 상대(고객·공급사)와 주고받는 서류 (v2.55.0 개명, 2026-08-05 사용자 확정).
 *   구 "문서함". 판매보고서 보관함은 결재를 거치는 사내 문서라 [사내결재 문서함]으로 이동.
 *
 * 만드는 문서 (우리가 작성·생성해 상대에게 보냄):
 *   - 견적서          (작성/목록/미리보기/Excel)
 *   - 발주서          (공급사 발주 — 우리가 발행)
 *   - 착수계 & 납품계  (착수계·납품계 각각 작성/목록/PDF)
 *   - 기술지원확약서   (작성/목록/PDF)
 *
 * 받은 문서 (고객·현장에서 수령 — 영업건 첨부 모아보기):
 *   - 고객 발주서      (영업건당 1개 — 등록 시 교체)
 *   - 매입견적서       (공급사에게서 받은 견적 · 영업건당 여러 개 · 보관만 — v2.137.0)
 *   - 분할납품요구서   (여러 개 허용)
 *   - 납품확인증 스캔본 (서명본 스캔 — 재고관리·모바일에서 업로드된 것 모아보기)
 *   (세금계산서 탭 — 발행요청 이력 + 웍스 봇 수신·처리 내역 — 은 v2.290.0 에 [영업진행] 안 탭으로 옮겼다 ·
 *    pages/sales/TaxInvoiceTab. 이 화면 탭 값은 주소에 안 실려 옛 링크가 없다.)
 *
 * 받은 문서는 영업건(entity_attachment, parent_kind='sales')에 첨부된 문서를 모아보고,
 * 등록 시 영업건에 연동(단일 원본 → 영업건 상세에서도 동일하게 다운로드, 중복 없음).
 *
 * 권한 (v2.50.0 — 전 계정 진입 · v2.53.0 — 지출·경비 그룹은 전용 메뉴 /expenses 로 이동):
 *   viewer 는 영업 문서 탭(견적서·발주서·관급내역서·정산서·고객발주서·매입견적서) 숨김.
 *   나머지 탭(착수계&납품계·기술지원확약서·분할납품요구서·납품확인증·기타관련서류)은
 *   열람·다운로드만 (작성·업로드 버튼은 각 탭의 canEdit 가드가 숨김 — 2026-08-04 확정).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { QuotesTab } from './documents/QuotesTab';
import { PurchaseOrdersTab } from './documents/PurchaseOrdersTab';
import { TechSupportTab } from './documents/TechSupportTab';
import { SalesDocsTab } from './documents/SalesDocsTab';
import { DeliveryStatementTab } from './documents/DeliveryStatementTab';
import { CommencementTab } from './documents/CommencementTab';
import { G2bDocsTab } from './documents/G2bDocsTab';
import { useAuth } from '../lib/auth-context';
import { PageTabs } from '../components/ui/PageTabs';

type DocKind =
  | 'quote'
  | 'po'
  | 'delivery'
  | 'support'
  | 'g2b-docs'
  | 'customer-po'
  | 'purchase-quote'
  | 'split-delivery'
  | 'delivery-scan'
  | 'misc-docs';

type TabDef = { key: DocKind; label: string; salesOnly?: boolean };

// 그룹 구분: 만드는 문서(작성·생성) / 받은 문서(수령 — 영업건 첨부 모아보기) / 지출·경비
//   salesOnly = viewer 숨김 (영업 금액이 담긴 문서 — 2026-08-04 사용자 확정 목록)
const TAB_GROUPS: { heading: string; tabs: TabDef[] }[] = [
  {
    heading: '만드는 문서',
    tabs: [
      { key: 'quote',    label: '견적서', salesOnly: true },
      { key: 'po',       label: '발주서', salesOnly: true },
      { key: 'delivery', label: '착수계 & 납품계' },
      { key: 'support',  label: '기술지원확약서' },
      // 관급내역서·정산서 보관본 (v2.230.0 · 개발 요청 #16) — 정산서에 요율이 있어 조회 전용 계정은 숨김(사용자 결정 10-06)
      { key: 'g2b-docs', label: '관급내역서·정산서', salesOnly: true },
    ],
  },
  {
    heading: '받은 문서',
    tabs: [
      { key: 'customer-po',    label: '고객 발주서', salesOnly: true },
      // 매입견적서 = 공급사에게서 받은 견적 (v2.137.0). 매입 단가가 담겨 viewer 숨김.
      { key: 'purchase-quote', label: '매입견적서', salesOnly: true },
      { key: 'split-delivery', label: '분할납품요구서' },
      { key: 'delivery-scan',  label: '납품확인증 스캔본' },
      // 세금계산서는 v2.290.0 에 영업진행 안 탭으로 옮겼다(사용자 결정 10-22)
    ],
  },
  {
    // 기타 — 관급내역서·정산서 자동 보관(작성 시 영업건 연동) + 카탈로그·확약서 등 수동 등록 (2026-07-28)
    heading: '기타',
    tabs: [{ key: 'misc-docs', label: '기타 관련서류' }],
  },
  // 지출·경비 그룹은 v2.53.0 에서 전용 메뉴(/expenses)로 이동
];

export function DocumentsPage() {
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  // viewer 는 영업 탭이 숨겨지므로 첫 노출 탭 = 착수계 & 납품계
  const [active, setActive] = useState<DocKind>(isViewer ? 'delivery' : 'quote');

  return (
    <div className="p-4 md:p-8">
      <div className="mb-3">
        <h1 className="ui-page-title">거래 문서함</h1>
        <p className="ui-page-sub">
          고객·공급사와 주고받는 서류를 한 곳에서 — 보내는 문서(견적서·발주서·납품 서류)와 받은 문서.
          {/* v2.55.0(08-05) 「판보 보관함이 옮겨졌습니다」 안내는 한 달 지나 정리 (v2.127.0) */}
        </p>
      </div>

      {/* 묶음 이름(만드는 문서 / 받은 문서 / 기타)은 탭 줄 안의 작은 글자로 — PageTabs `group` */}
      <PageTabs
        tabs={TAB_GROUPS.flatMap((g) =>
          g.tabs
            .filter((t) => !(isViewer && t.salesOnly))
            .map((t) => ({ key: t.key, label: t.label, group: g.heading })),
        )}
        value={active}
        onChange={setActive}
        ariaLabel="거래 문서함 보기"
      />

      {/* 다른 메뉴에서 만드는 문서 안내 — 발견성 (2026-07-19). viewer 는 해당 메뉴가 없어 숨김 */}
      {!isViewer && (
      <div className="mb-4 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
        💡 <b>관급내역서·정산서</b>는{' '}
        <Link to="/g2b" className="text-blue-600 underline">나라장터쇼핑몰 현황</Link>에서 만들며,
        만들 때마다 <b>[관급내역서·정산서]</b> 탭에 보관되고, 영업건을 연동하면 그 영업건 <b>[기타 관련서류]</b>에도 사본이 붙습니다.{' '}
        <b>영업팀 월간보고서</b>는{' '}
        <Link to="/analysis" className="text-blue-600 underline">매출 분석</Link> 헤더에서,{' '}
        <b>납품확인증(생성)·검수명판</b>은 영업건 상세 또는 재고관리 [출고 관리]에서 만듭니다.
      </div>
      )}

      {isViewer && ['quote', 'po', 'customer-po', 'purchase-quote', 'g2b-docs'].includes(active) ? (
        // viewer 가 URL 상태 등으로 영업 문서 탭에 닿아도 렌더하지 않음 (이중 방어)
        <StartAndDeliveryTab />
      ) : active === 'quote' ? (
        <QuotesTab />
      ) : active === 'po' ? (
        <PurchaseOrdersTab />
      ) : active === 'delivery' ? (
        <StartAndDeliveryTab />
      ) : active === 'support' ? (
        <TechSupportTab />
      ) : active === 'g2b-docs' ? (
        <G2bDocsTab />
      ) : active === 'customer-po' ? (
        <SalesDocsTab kind="발주서" singlePerSales noun="고객 발주서" />
      ) : active === 'purchase-quote' ? (
        <SalesDocsTab
          kind="매입견적서"
          singlePerSales={false}
          noun="매입견적서"
          accept=".pdf,.xlsx,.xlsm,.xls"
          fileLabel="PDF·엑셀"
        />
      ) : active === 'split-delivery' ? (
        <SalesDocsTab kind="분할납품요구서" singlePerSales={false} noun="분할납품요구서" />
      ) : active === 'delivery-scan' ? (
        <SalesDocsTab kind="납품확인증" singlePerSales={false} noun="납품확인증 스캔본" />
      ) : (
        <SalesDocsTab
          kind="기타 관련서류"
          singlePerSales={false}
          noun="기타 관련서류"
          accept=".pdf,.xlsx,.xls,.png,.jpg,.jpeg,.gif"
          fileLabel="PDF·엑셀·이미지"
        />
      )}
    </div>
  );
}

/**
 * 착수계 & 납품계 통합 탭.
 *   - 착수계: CommencementTab (작성·목록·미리보기 — 분납서 → 9종 양식 PDF).
 *   - 납품계: 기존 DeliveryStatementTab(작성·목록·미리보기) 그대로.
 */
function StartAndDeliveryTab() {
  return (
    <div className="space-y-4">
      <CommencementTab />
      <DeliveryStatementTab />
    </div>
  );
}
